import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Select, { components } from "react-select";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { api } from "@/helper/api";
import styles from "@/components/tables/fields/crudSelectField.module.css";

interface RoleOption {
  value: number;
  label: string;
  fixed?: boolean;
  companyAssigned?: boolean;
}

interface RoleReference {
  id: number;
  name: string;
}

interface Props {
  effectiveRolesOfEditedUser: RoleReference[];
  directRolesOfEditedUser: RoleReference[];
  companyId?: string;
}

const roleOption = (role: any): RoleOption => ({
  value: Number(role.id ?? role.value ?? role),
  label: role.name ?? role.label ?? String(role),
});

export default function RoleSelect({
  effectiveRolesOfEditedUser,
  directRolesOfEditedUser,
  companyId,
}: Props) {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const [roleResponse, setRoleResponse] = useState<{
    companyId?: string;
    roles: RoleOption[];
  }>({ roles: [] });
  const additionalRoles =
    roleResponse.companyId === companyId ? roleResponse.roles : [];
  useEffect(() => {
    let superseded = false;
    api.genericApiRequest({
      entity: "group",
      method: "get",
      parametersToRender: {
        additionalUrlParameters: {
          is_additional: true,
          assignment_company_id: companyId ?? "",
        },
      },
      getAllPages: true,
      successHandler: (response: any) => {
        if (superseded) return;
        setRoleResponse({
          companyId,
          roles: (response.data.results || response.data).map(roleOption),
        });
      },
    });
    return () => {
      superseded = true;
    };
  }, [companyId]);
  const savedDirectRoleIds = new Set(
    directRolesOfEditedUser.map((role) => Number(role.id))
  );
  const companyAssignedRoles = effectiveRolesOfEditedUser
    .map(roleOption)
    .filter((role) => !savedDirectRoleIds.has(role.value))
    .map((role) => ({ ...role, fixed: true, companyAssigned: true }));
  const companyAssignedRoleIds = new Set(
    companyAssignedRoles.map((role) => role.value)
  );
  const assignableRoles = additionalRoles.filter(
    (role) => !companyAssignedRoleIds.has(role.value)
  );
  const additionalRoleIds = new Set(assignableRoles.map((role) => role.value));

  return (
    <Controller
      name="direct_group_set"
      control={control}
      render={({ field }) => {
        const selectedRoles: RoleOption[] = (field.value || [])
          .map(roleOption)
          .filter(
            (role: RoleOption) => !companyAssignedRoleIds.has(role.value)
          );
        const fixedRoles = selectedRoles
          .filter((role) => !additionalRoleIds.has(role.value))
          .map((role) => ({ ...role, fixed: true }));
        const selectedAdditionalRoles = selectedRoles.filter((role) =>
          additionalRoleIds.has(role.value)
        );
        return (
          <div className={`${styles.selectWrapper} ${styles.maxWidth}`}>
            <label className={styles.dropdownWithRefLabel}>{t("groups")}</label>
            <Select
              isMulti
              options={assignableRoles}
              value={[
                ...fixedRoles,
                ...companyAssignedRoles,
                ...selectedAdditionalRoles,
              ]}
              isClearable={false}
              isSearchable
              placeholder={t("select_placeholder")}
              menuPortalTarget={document.body}
              components={{
                MultiValueRemove: (props) =>
                  props.data.fixed ? null : (
                    <components.MultiValueRemove {...props} />
                  ),
                MultiValueLabel: (props) =>
                  props.data.fixed ? (
                    <Tooltip
                      title={t(
                        props.data.companyAssigned
                          ? "role_assigned_by_company"
                          : "mandatory_role"
                      )}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                        }}
                      >
                        {props.children}
                        {props.data.companyAssigned ? (
                          <span style={{ color: "#8c1ec8" }}>ⓘ</span>
                        ) : (
                          <InfoOutlinedIcon fontSize="inherit" />
                        )}
                      </span>
                    </Tooltip>
                  ) : (
                    <components.MultiValueLabel {...props} />
                  ),
              }}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                control: (base) => ({
                  ...base,
                  borderRadius: "0.75rem",
                  borderColor: "rgba(0, 0, 0, 0.23)",
                }),
                valueContainer: (base) => ({
                  ...base,
                  padding: "0.75rem",
                  borderRadius: "0.75rem",
                }),
                dropdownIndicator: (base) => ({
                  ...base,
                  color: "#ba78de",
                  backgroundColor: "#f4f2f2",
                  borderRadius: "0.75rem",
                  padding: "4px",
                  margin: "0 0.3rem",
                }),
                multiValue: (base, state) => ({
                  ...base,
                  backgroundColor: state.data.fixed ? "#bcbcbc" : "#8c1ec8",
                  borderRadius: "0.75rem",
                  color: "white",
                  padding: "0.25rem 0.5rem 0.25rem 0.25rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.25rem",
                }),
                multiValueLabel: (base) => ({ ...base, color: "white" }),
                multiValueRemove: (base) => ({
                  ...base,
                  borderRadius: "0.75rem",
                  color: "#8c1ec8",
                  backgroundColor: "white",
                  padding: "0.025rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  ":hover": { color: "#ff1744" },
                }),
              }}
              onChange={(nextRoles) => {
                const editableRoles = (nextRoles as RoleOption[]).filter(
                  (role) => additionalRoleIds.has(role.value)
                );
                field.onChange(
                  [...fixedRoles, ...editableRoles].map((role) => ({
                    id: role.value,
                    name: role.label,
                  }))
                );
              }}
            />
          </div>
        );
      }}
    />
  );
}
